import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { productsData } from "../../mocks/productsData";
import { fetchProducts, type ProductItem } from "../../api";

const PRODUCTS_PER_PAGE = 12;

export default function Products() {
  const navigate = useNavigate();
  const productsRef = useRef<HTMLDivElement>(null);
  const [activeCategory, setActiveCategory] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [apiProducts, setApiProducts] = useState<ProductItem[]>([]);
  const [apiCategories, setApiCategories] = useState<{ name: string; slug: string }[]>([]);
  const [apiTotal, setApiTotal] = useState(0);
  const [apiTotalPages, setApiTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);

  useEffect(() => {
    const cat = activeCategory === "All" ? undefined : activeCategory;
    setLoading(true);
    fetchProducts({ category: cat, page: currentPage, limit: PRODUCTS_PER_PAGE })
      .then((res) => {
        setApiProducts(res.items);
        setApiCategories(res.categories);
        setApiTotal(res.total);
        setApiTotalPages(res.totalPages);
        setApiError(false);
      })
      .catch((err) => {
        console.warn("产品数据加载失败:", err);
        setApiError(true);
      })
      .finally(() => setLoading(false));
  }, [activeCategory, currentPage]);

  // 切换分页或分类时滚动到产品列表顶部
  useEffect(() => {
    if (productsRef.current) {
      productsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [currentPage, activeCategory]);

  // 只有 API 真正失败时才用假数据兜底，API 返回空列表表示暂无数据
  const displayProducts = apiError ? productsData.items : apiProducts;
  const categories = apiError
    ? productsData.categories
    : ["All", ...apiCategories.map((c) => c.name)];

  // 服务端已分页，API 正常时直接用返回数据；降级到 mock 数据时才做客户端分页
  const totalProducts = apiError ? productsData.items.length : apiTotal;
  const totalPages = apiError
    ? Math.ceil(productsData.items.length / PRODUCTS_PER_PAGE)
    : apiTotalPages;
  const paginatedProducts = apiError
    ? productsData.items.slice(
        (currentPage - 1) * PRODUCTS_PER_PAGE,
        currentPage * PRODUCTS_PER_PAGE
      )
    : displayProducts;

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Professional%20walkie-talkie%20two-way%20radio%20collection%20product%20display%20multiple%20devices%20arranged%20on%20dark%20surface%20blue%20and%20green%20accent%20lighting%20professional%20product%20photography%20high%20quality%20studio%20lighting&width=1400&height=800&seq=40&orientation=landscape"
              alt="Huatera products"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-dark/60 to-dark/30"></div>
          </div>
          <div className="relative z-10 h-full flex flex-col justify-center px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-1.5 bg-white/20 rounded-full mb-6">
                <span className="text-white text-sm font-medium">Our Products</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
                Professional Grade<br />
                <span className="text-secondary">Two-Way Radios</span>
              </h1>
              <p className="text-white/80 text-lg mt-4 max-w-2xl">
                Browse our complete range of two-way radios and accessories. From entry-level to mission-critical.
              </p>
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-8 px-4 md:px-8 lg:px-12 border-b border-gray-100 bg-light-gray">
          <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-3">
            <span className="text-gray-500 text-sm mr-2">Filter:</span>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-primary text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                }`}
              >
                {cat}
              </button>
            ))}
            <span className="text-gray-400 text-sm ml-auto">
              {totalProducts} products
            </span>
          </div>
        </section>

        {/* Products Grid */}
        <section ref={productsRef} className="py-12 md:py-16 px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedProducts.map((product) => (
                <div
                  key={product.id}
                  onClick={() => navigate(`/products/${product.id}`)}
                  className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-primary/30 transition-all duration-300 cursor-pointer"
                  data-product-shop
                >
                  <div className="aspect-square overflow-hidden bg-light-gray relative">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-dark font-bold text-lg mb-1">{product.name}</h3>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                        In Stock
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
                      currentPage === page
                        ? "bg-primary text-white"
                        : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
                <span className="text-gray-400 text-sm ml-3">
                  Page {currentPage} of {totalPages}
                </span>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}